import type {
  AiOptStatus,
  AiVisibilityOptimizationsDisabled,
} from "~/components/siteSettings/AiVisibilityOptimizationsPanel";

/**
 * Derive the AI Visibility Engine UI status from the two backend columns.
 *
 * `site_speed_status` now defaults to "pending" on the backend even for sites
 * that never applied (auto-approval flows can also set a status without an
 * explicit application). So "pending" alone no longer means "under review" —
 * we only treat it that way when the application was actually requested
 * (`site_speed_requested`). Otherwise we show the apply prompt ("null").
 */
export const getAiOptStatus = (
  siteSpeedStatus: "pending" | "approved" | "rejected" | null | undefined,
  siteSpeedRequested: boolean | undefined,
): AiOptStatus => {
  if (siteSpeedStatus === "pending") {
    return siteSpeedRequested ? "pending" : "null";
  }
  if (siteSpeedStatus === "rejected") return "rejected";
  if (siteSpeedStatus == null) return "null";

  return "approved";
};

export const getAiOptDisabled = (
  canUpdateSite: boolean,
  canEditCrawlSettings: boolean,
): AiVisibilityOptimizationsDisabled => ({
  site_speed_requested: !canUpdateSite,

  prerendering: !canUpdateSite || !canEditCrawlSettings,
  apex_redirect: !canUpdateSite,
  inject_widget: !canUpdateSite,
  inject_markdown: !canUpdateSite,

  disable_lazy_automation: !canUpdateSite,
  disable_defer_automation: !canUpdateSite,
  disable_preload_automation: !canUpdateSite,
});

type StatusUI = {
  notification?: {
    type: "warning" | "info" | "success" | "danger";
    message: string;
  };
  instructionText?: string;
};

export const AI_VISIBILITY_STATUS_UI: Record<AiOptStatus, StatusUI> = {
  pending: {
    notification: {
      type: "warning",
      message: "Your application is under review.",
    },
  },
  rejected: {
    notification: {
      type: "danger",
      message:
        "Unfortunately, our specialists have reviewed your request and, at this time, it is not technically possible to provide this service, as it may cause issues affecting your website's functionality. We are continuously working on improvements and will notify you as soon as they become available.",
    },
    instructionText: "",
  },
  null: {
    notification: {
      type: "info",
      message:
        "The feature is currently in testing and may not work with all websites. Toggle the switch to apply for AI Visibility Engine Optimization. Our team will assess the technical feasibility and contact you via email.",
    },
  },
  approved: {},
};
